import { ReactElement } from 'react'
import { Formik, getIn } from 'formik'
import { useIntl, FormattedMessage, defineMessages } from 'react-intl'
import { isEmpty } from 'lodash'

import { Button } from 'components/v2/Button'
import Card from '@10xLabs/react-web/components/Card'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import { Row } from '@10xLabs/react-web/components/Row'
import { Column } from '@10xLabs/react-web/components/Column'
import { Label } from 'components/v2/Label'
import Heading from 'components/v2/Heading'
import { Switch } from 'components/v2/Switch'
import { InputCurrency } from 'components/InputCurrency'
import { getUnprocessableErrors } from 'utils/error'
import { sanitizePrice } from 'utils/math'
import { getDifference } from 'utils/object'
import { schema } from './schema'
import { SelectSalesChannelsV2 } from 'features/Services/components/SelectSalesChannelsV2'
import { IInsuranceForm, IInsuranceSettingsForm } from './types'
import { ISalesChannel } from 'features/Services/components/NewServicesV2/components/OfferSettingsCard/types'

const messages = defineMessages({
  salesChannelsPlaceholder: {
    id: 'ancillaries.settings.form.salesChannels.placeholder',
    defaultMessage: 'Select a sales channel'
  }
})

const Form = ({
  values,
  touched,
  errors,
  handleBlur,
  setFieldValue,
  handleSubmit,
  error,
  loading,
  hasUpdated,
  disabled
}: IInsuranceForm): ReactElement => {
  const intl = useIntl()

  const handleSalesChannelsChange = (salesChannels: ISalesChannel[]): void => {
    setFieldValue('salesChannels', salesChannels)
  }

  return (
    <form autoComplete="off" onSubmit={handleSubmit}>
      <Card.Content noHeader>
        <Row>
          <Column>
            <Heading.H5>
              <FormattedMessage id="ancillaries.insurance.form.title" defaultMessage="Insurance" />
            </Heading.H5>
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <Label>
              <FormattedMessage id="ancillaries.insurance.form.active.label" defaultMessage="Visible" />
            </Label>
            <Switch
              value={values.active}
              disabled={disabled}
              onChange={(value: boolean): void => {
                if (!disabled) {
                  setFieldValue('active', value)
                }
              }}
            />
          </Column>
        </Row>
        <Row>
          <Column>
            <Label>
              <FormattedMessage id="ancillaries.insurance.form.salesChannels.label" defaultMessage="Sales Channels" />
            </Label>
            <SelectSalesChannelsV2
              id="salesChannels"
              values={values.salesChannels}
              placeholder={intl.formatMessage(messages.salesChannelsPlaceholder)}
              onChange={handleSalesChannelsChange}
              touched={getIn(touched, 'salesChannels')}
              errors={getIn(errors, 'salesChannels')}
              disabled={disabled}
            />
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <Label>
              <FormattedMessage id="ancillaries.insurance.form.price.label" defaultMessage="Price" />
            </Label>
            <InputCurrency
              id="price"
              suffix=""
              value={sanitizePrice(values.price)}
              placeholder="$0.00"
              onChange={(value: string): void => {
                setFieldValue('price', value)
              }}
              onBlur={handleBlur}
              touched={touched.price}
              errors={errors.price}
              disabled={disabled}
              hideSuffix
            />
          </Column>
        </Row>
      </Card.Content>

      <Card.Footer>
        {error && (
          <div>
            <ErrorMessage errors={getUnprocessableErrors(error)}>
              <FormattedMessage
                id="ancillaries.insurance.form.error"
                defaultMessage="There was an error while updating the insurance settings"
              />
            </ErrorMessage>
          </div>
        )}

        <Card.FooterActions>
          <Button type="submit" kind="primary" loading={loading} disabled={disabled || !hasUpdated}>
            {hasUpdated ? (
              <FormattedMessage id="dictionary.update" defaultMessage="Update" />
            ) : (
              <FormattedMessage id="dictionary.updated" defaultMessage="Updated" />
            )}
          </Button>
        </Card.FooterActions>
      </Card.Footer>
    </form>
  )
}

export const InsuranceSettingsForm = ({
  settings = {},
  onSubmit,
  error,
  loading,
  disabled
}: IInsuranceSettingsForm): ReactElement => {
  const intl = useIntl()

  const initialSettings = {
    active: settings?.active ?? false,
    salesChannels: settings?.salesChannelsV2 ?? [],
    price: sanitizePrice(settings?.price ? settings.price / 100 : 0)
  }

  return (
    <Formik initialValues={initialSettings} validationSchema={schema(intl)} onSubmit={onSubmit}>
      {(props): ReactElement => {
        const previousSettings = {
          active: settings.active,
          price: settings.price,
          salesChannelIDs: settings.salesChannelsV2?.map(({ id }) => id) ?? []
        }
        const newSettings = {
          active: props.values.active,
          price: sanitizePrice(props.values.price * 100, true),
          salesChannelIDs: props.values.salesChannels.map(({ id }) => id)
        }

        const payload = getDifference(newSettings, previousSettings)

        const hasUpdated = !isEmpty(payload)

        return <Form {...props} hasUpdated={hasUpdated} error={error} loading={loading} disabled={disabled} />
      }}
    </Formik>
  )
}
